// HAND-ROLLED — documented exception per
// feedback_deterministic_codegen_over_hand_rolling.md. Same rationale as
// activity-feed-list.ts.
//
// CHG-1643 / Wave 1 Tier 1 — port of v1 DeliveryLogs.tsx. Backend handler
// scopes by X-Org-Context (resolve_org_id); no scope= param.

import type { ListPageConfig } from "../components/composition/ListPage";
import { deliveryLogsApi, type DeliveryLogEntry } from "../api/delivery-logs";
import type { ReactNode } from "react";

const STATUS_LABEL: Record<string, string> = {
  queued: "Queued",
  sent: "Sent",
  failed: "Failed",
  bounced: "Bounced",
  skipped: "Skipped",
  queued_for_topup: "Awaiting top-up",
};

function statusColumn(row: DeliveryLogEntry): ReactNode {
  return STATUS_LABEL[row.status] ?? row.status;
}

function templateColumn(row: DeliveryLogEntry): ReactNode {
  return row.template_slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export const deliveryLogsListConfig: ListPageConfig<DeliveryLogEntry> = {
  title: "Delivery Log",
  subtitle: "Every notification dispatched to donors and team members — track status, debug failures.",
  queryKey: ["delivery-logs"] as const,
  queryFn: ({ page, pageSize, search }) =>
    deliveryLogsApi.listDeliveryLogs({
      page,
      pageSize,
      recipient: search,
    }),
  defaultSort: { field: "created_at", direction: "desc" },
  pageSize: 50,
  search: { enabled: true, placeholder: "Search by recipient email…" },
  emptyState: {
    title: "No delivery logs yet",
    subtitle: "Delivery logs appear here when the system sends notifications to donors and team members.",
  },
  columns: [
    // Sent-at first because that's the field operators want to see — the
    // "did it actually deliver" answer. Falls back to em-dash for queued
    // / never-attempted rows so the "Queued" column below disambiguates.
    { field: "sent_at", label: "Sent", type: "datetime", sortable: true },
    { field: "created_at", label: "Queued", type: "datetime", sortable: true },
    { field: "recipient_email", label: "Recipient", type: "email" },
    { field: "template_slug", label: "Template", type: "custom", render: templateColumn },
    { field: "subject", label: "Subject", type: "text" },
    { field: "status", label: "Status", type: "custom", render: statusColumn },
  ],
};
